import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Container, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { isNotEmptyArray } from 'src/services/validationService';
import { USER_LIST } from 'src/utils/constants';
import { lastNameName, email, group } from 'src/utils/label';
import { buildFullName } from 'src/utils/utils';
import { tryGetUserDetail, tryGetListUserXApplicationXProfile, tryGetListUserProfile } from 'src/redux/user/userActionCreator';
import { getUserXApplicationXProfileByUserId, getUserToDetail, getUserProfileListData } from 'src/redux/user/userReducer';
import { getListAllServiceAdministrativeByUserData, getAdministrativeServiceListIsFetching } from "src/redux/administrativeService/administrativeServiceReducer";
import { tryListAllServiceAdministrativeByUser } from "src/redux/administrativeService/administrativeServiceActionCreator";
import { clearListAllServiceAdministrativeByUser } from "src/redux/administrativeService/administrativeServiceActions";
import { useJurisdictions } from './utils/useJurisdictions';
import AppLoading from '../../common/AppLoading';

const UserDetailsPage = () => {
	const dispatch = useDispatch();
	const { 
        fetchUserOrganisms, 
        userOrganisms, 
        userOrganismsLoading, 
        userOrganismsError 
    } = useJurisdictions();

	// Datos del Usuario
	const userData = useSelector(state => getUserToDetail(state));
	const userProfileList = useSelector(state => getUserXApplicationXProfileByUserId(state, userData?.id, userData?.profileId));
	const listProfileData = useSelector(state => getUserProfileListData(state, userProfileList?.profileId));
	const userProfile = listProfileData?.find(item => item.id == userProfileList?.profileId)

	// Datos del Servicio
	const servicesListRaw = useSelector((state) => getListAllServiceAdministrativeByUserData(state));
	const servicesList = servicesListRaw?.sort((a, b) => {
		return a.code > b.code ? 1 : -1;
	});
	const servicesListIsFetching = useSelector((state) =>getAdministrativeServiceListIsFetching(state));
	const hasServicesList = isNotEmptyArray(servicesList);

	// Estados para controlar si los datos ya fueron cargados
	const [servicesLoaded, setServicesLoaded] = useState(false);
	const [organismsLoaded, setOrganismsLoaded] = useState(false);
	const isLoadingUserData = !userData?.id || !userProfile;
	const isLoadingServices = servicesListIsFetching || !servicesLoaded;
	const isLoadingOrganisms = userOrganismsLoading || !organismsLoaded;
	const isLoading = isLoadingUserData || isLoadingServices || isLoadingOrganisms;

	const onClickBackButton = () => {
		dispatch(push(USER_LIST));
	};

	useEffect(() => {
		if (!servicesListIsFetching && (hasServicesList || servicesList !== undefined)) {
			setServicesLoaded(true);
		}
	}, [servicesListIsFetching, hasServicesList, servicesList]);

	useEffect(() => {
		if (!userOrganismsLoading && (userOrganisms !== null || userOrganismsError)) {
			setOrganismsLoaded(true);
		}
	}, [userOrganismsLoading, userOrganisms, userOrganismsError]);

    useEffect(() => {
        setServicesLoaded(false);
        setOrganismsLoaded(false);

        if (userData?.id) {
            dispatch(tryGetUserDetail(userData.id));
            dispatch(tryGetListUserXApplicationXProfile());
            dispatch(tryGetListUserProfile());
            dispatch(clearListAllServiceAdministrativeByUser());
            dispatch(tryListAllServiceAdministrativeByUser(userData?.id));
            fetchUserOrganisms(userData.id);
        }
    }, [userData?.id, dispatch, fetchUserOrganisms]);

	return (
		<Container fluid className='mb-5'>
			<Card className='mb-3'>
				<Card.Header className='h5'>
					Detalle del usuario
				</Card.Header>

				{/* DATOS DEL USUARIO EN COLUMNAS */}
				<Table className='text-black-color' striped hover>
					<tbody>
                        <tr>
                            <td className='text-center font-weight-bold'>
                                {lastNameName}:
                            </td>
                            <td className='text-center'>
                                {buildFullName(userData?.name) || '-'}
                            </td>
                        </tr>
                        <tr>
                            <td className='text-center font-weight-bold'>
                                {email}:
                            </td>
                            <td className='text-center'>
                                {userData?.email || '-'}
                            </td>
                        </tr>
                        <tr>
                            <td className='text-center font-weight-bold'>
                                {group}:
                            </td>
                            <td className='text-center'>
                                {userProfile?.name || '-'}
                            </td>
                        </tr>
					</tbody>
				</Table>

				{/* SECCIÓN DE SERVICIOS */}
				<h5 className='mt-3 ml-3'>Servicios asociados al usuario</h5>
				<Table striped hover>
					<thead>
						<tr>
							<th className="text-center align-middle">Código</th>
							<th className="text-center align-middle">Descripción</th>
							<th className="text-center align-middle">Tipo</th>
						</tr>
					</thead>
					<tbody className="text-black-color">
						{hasServicesList ? (
							<>
								{servicesList?.map((service) => (
									<tr key={service?.id}>
										<td className="text-center align-middle">
											{service?.code}
										</td>
										<td className="text-center align-middle">
											{service?.name}
										</td>
										<td className="text-center align-middle">
											{service?.administrativeServiceType?.name}
										</td>
									</tr>
								))}
							</>
						) : (
							<tr>
								<td colSpan="3" className="text-center">
									{servicesListIsFetching ? (
										<Spinner
											animation="border"
											variant="success"
											className="my-3"
										/>
									) : (
										<Alert variant="info" className="mb-0">
											<FontAwesomeIcon icon={faExclamationTriangle} className="text-black-color mr-3"/>
											No hay registros
										</Alert>
									)}
								</td>
							</tr>
						)}
					</tbody>
				</Table>

				{/* SECCIÓN DE ORGANISMOS */}
				<h5 className='mt-3 ml-3'>Organismos asociados al usuario</h5>
				<Table striped hover>
					<thead>
						<tr>
							<th className="text-center align-middle">ID</th>
							<th className="text-center align-middle">Nombre</th>
						</tr>
					</thead>
					<tbody className="text-black-color">
						{userOrganisms && userOrganisms.length > 0 ? (
							userOrganisms.map((organism) => (
								<tr key={organism?.id}>
									<td className="text-center align-middle">
										{organism?.codeBudget}
									</td>
									<td className="text-center align-middle">
										{organism?.name}
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan="2" className="text-center">
									{userOrganismsLoading && 
										<Spinner
											animation="border"
											variant="success"
											className="my-3"
										/>
									}
									{(!userOrganismsLoading && userOrganismsError) &&
										<Alert variant="danger" className="mb-0">
											<FontAwesomeIcon icon={faExclamationTriangle} className="text-black-color mr-3"/>
											Error al cargar organismos
										</Alert>
									}
									{(!userOrganismsLoading && !userOrganismsError) &&
										<Alert variant="info" className="mb-0">
											<FontAwesomeIcon icon={faExclamationTriangle} className="text-black-color mr-3"/>
											No hay organismos asociados
										</Alert>
									}
								</td>
							</tr>
						)}
					</tbody>
				</Table>

				<div className='text-center'>
					<Button variant='primary' size='lg' className='my-3' onClick={onClickBackButton}>
						<FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
						Volver
					</Button>
				</div>
			</Card>
			<AppLoading isLoading={isLoading} />
		</Container>
	)
};

export default UserDetailsPage;